import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Label } from "../components/ui/label";
import { Brain, ChevronLeft, ChevronRight, CheckCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function IQTestPage() {
  const { participantId } = useParams();
  const navigate = useNavigate();
  const startTimeRef = useRef(null);
  
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchQuestions();
  }, []);

  useEffect(() => {
    // Start timer when questions are loaded
    if (questions.length > 0 && !startTimeRef.current) {
      startTimeRef.current = Date.now();
    }
  }, [questions]);

  const fetchQuestions = async () => {
    try {
      const response = await axios.get(`${API}/iq-questions`);
      setQuestions(response.data.questions);
    } catch (error) {
      toast.error("Gagal memuat soal");
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (value) => {
    setAnswers(prev => ({
      ...prev,
      [questions[currentIndex].id]: value
    }));
  };

  const goToNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(prev => prev + 1);
    }
  };

  const goToPrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    const answeredCount = Object.keys(answers).length;
    if (answeredCount < questions.length) {
      const confirm = window.confirm(
        `Anda baru menjawab ${answeredCount} dari ${questions.length} soal. Yakin ingin submit?`
      );
      if (!confirm) return;
    }

    setSubmitting(true);
    try {
      const formattedAnswers = Object.entries(answers).map(([questionId, answer]) => ({
        question_id: parseInt(questionId),
        answer: answer
      }));

      // Calculate time spent in seconds
      const timeSpent = startTimeRef.current 
        ? Math.floor((Date.now() - startTimeRef.current) / 1000) 
        : 0;

      await axios.post(`${API}/iq-test/submit`, {
        participant_id: participantId,
        answers: formattedAnswers,
        time_spent: timeSpent
      });

      toast.success("Test IQ selesai! Lanjut ke test mengetik.");
      navigate(`/typing-test/${participantId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Gagal menyimpan jawaban");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || submitting) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
        <div className="text-center">
          <Brain className="w-12 h-12 text-[#002FA7] mx-auto mb-4 animate-pulse" />
          <p className="text-[#475569]">{submitting ? "Menyimpan jawaban..." : "Memuat soal..."}</p>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentIndex];
  const progress = ((currentIndex + 1) / questions.length) * 100;
  const answeredCount = Object.keys(answers).length;

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-white/80 backdrop-blur-xl border-b border-slate-200/50">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Brain className="w-5 h-5 text-[#002FA7]" />
              <span className="font-bold text-[#0A0A0A]">Test IQ</span>
            </div>
            <span className="text-sm font-mono text-[#475569]" data-testid="question-counter">
              {currentIndex + 1} / {questions.length}
            </span>
          </div>
          <Progress value={progress} className="h-1" data-testid="progress-bar" />
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-4 py-8">
        <div className="bg-white border border-slate-200 p-6 md:p-8 animate-fadeIn" key={currentIndex}>
          {/* Question */}
          <div className="mb-8">
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-2 block">
              Soal {currentIndex + 1}
            </span>
            <h2 
              className="text-xl sm:text-2xl font-bold text-[#0A0A0A] leading-relaxed"
              data-testid="question-text"
            >
              {currentQuestion.question}
            </h2>
          </div>

          {/* Options */}
          <RadioGroup
            value={answers[currentQuestion.id] || ""}
            onValueChange={handleAnswerChange}
            className="space-y-3"
          >
            {currentQuestion.options.map((option, idx) => (
              <div
                key={idx}
                className={`flex items-center space-x-3 p-4 border rounded-sm transition-colors cursor-pointer ${
                  answers[currentQuestion.id] === option
                    ? "border-[#002FA7] bg-blue-50"
                    : "border-slate-200 hover:border-slate-300"
                }`}
                onClick={() => handleAnswerChange(option)}
              >
                <RadioGroupItem
                  value={option}
                  id={`option-${idx}`}
                  data-testid={`iq-option-${String.fromCharCode(97 + idx)}`}
                />
                <Label
                  htmlFor={`option-${idx}`}
                  className="flex-1 cursor-pointer text-[#0A0A0A]"
                >
                  {option}
                </Label>
              </div>
            ))}
          </RadioGroup>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-200">
            <Button
              variant="outline"
              onClick={goToPrev}
              disabled={currentIndex === 0}
              className="rounded-sm"
              data-testid="btn-prev"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Sebelumnya
            </Button>

            {currentIndex === questions.length - 1 ? (
              <Button
                onClick={handleSubmit}
                disabled={submitting}
                className="bg-[#002FA7] hover:bg-[#00237C] rounded-sm"
                data-testid="btn-submit-iq"
              >
                {submitting ? (
                  "Menyimpan..."
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Submit Test IQ
                  </>
                )}
              </Button>
            ) : (
              <Button
                onClick={goToNext}
                className="bg-[#002FA7] hover:bg-[#00237C] rounded-sm"
                data-testid="btn-next"
              >
                Selanjutnya
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>
        </div>

        {/* Question Navigator */}
        <div className="mt-6 bg-white border border-slate-200 p-4">
          <p className="text-sm text-[#475569] mb-3">
            Jawaban: <span className="font-semibold text-[#002FA7]">{answeredCount}</span> / {questions.length}
          </p>
          <div className="flex flex-wrap gap-2">
            {questions.map((q, idx) => (
              <button
                key={q.id}
                onClick={() => setCurrentIndex(idx)}
                className={`w-9 h-9 text-sm font-mono rounded-sm transition-colors ${
                  idx === currentIndex
                    ? "bg-[#002FA7] text-white"
                    : answers[q.id]
                    ? "bg-green-100 text-green-700 border border-green-300"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
                data-testid={`nav-question-${idx + 1}`}
              >
                {idx + 1}
              </button>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
